import { useEffect, useId, useState } from "react";
import {
  buildSmartDropUrl,
  buildSmartPosUrl,
  buildSmartSignUrl,
  createSmartDropLink,
  DOC_CATEGORY_LABELS,
  fetchSmartDropInbox,
  FOUR_LEGS_LABELS,
  getCachedSmartDropItems,
  groupItemsByBorrower,
  LAYA_FAMILY_LABELS,
  PORTAL_ROLE_LABELS,
  previewFileClassification,
  REVIEW_STATUS_LABELS,
  ROUTED_SEAT_LABELS,
  updateSmartDropItem,
  UPLOADER_ROLE_LABELS,
  uploadStaffSmartDrop,
  type LayaDocumentFamily,
  type PageIntegrityStatus,
  type PortalRole,
  type RoutedSeat,
  type SmartDropItem,
  type SmartDropLink,
  type UploaderRole,
} from "../lib/smartDrop";

export interface LoSmartDropButtonProps {
  personId: string;
  loName: string;
  companyId?: string | null;
  companyName?: string | null;
  dealLinkId?: string | null;
  compact?: boolean;
}

const PORTAL_ROLE_OPTIONS: { role: PortalRole; label: string }[] = [
  { role: "broker_lo", label: "Broker / LO Link" },
  { role: "loan_processor", label: "Loan Processor Link" },
  { role: "borrower", label: "Borrower Link" },
];

const STAFF_ROLE_OPTIONS: { role: UploaderRole; label: string }[] = [
  { role: "ae", label: "Account Executive (AE)" },
  { role: "ca", label: "Credit Analyst (CA)" },
  { role: "am", label: "Account Manager (AM)" },
  { role: "processor", label: "Loan Processor" },
];

function seatBadgeTone(seat: RoutedSeat): { bg: string; fg: string; border: string } {
  switch (seat) {
    case "AE":
      return { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" };
    case "CA":
      return { bg: "#f5f3ff", fg: "#6d28d9", border: "#ddd6fe" };
    case "AM":
      return { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0" };
    case "Processor":
      return { bg: "#fffbeb", fg: "#b45309", border: "#fde68a" };
  }
}

function getItemLayaFamily(item: SmartDropItem): string {
  const raw =
    item.extracted_highlights?.laya_family ??
    item.laya_family ??
    "other";
  if (raw in LAYA_FAMILY_LABELS) {
    return LAYA_FAMILY_LABELS[raw as LayaDocumentFamily];
  }
  return `Laya · ${String(raw)}`;
}

function getItemStatementPeriod(item: SmartDropItem): string | null {
  const period =
    item.extracted_highlights?.statement_period ??
    item.statement_period ??
    null;
  return typeof period === "string" && period.trim() ? period.trim() : null;
}

function isSegmentationHold(item: SmartDropItem): boolean {
  return Boolean(
    item.extracted_highlights?.segmentation_guard_triggered ??
      item.segmentation_guard_triggered ??
      (item.extracted_highlights?.laya_family === "mixed_page" ||
        item.laya_family === "mixed_page"),
  );
}

function getItemPageIntegrity(item: SmartDropItem): {
  status: PageIntegrityStatus;
  label: string;
  bg: string;
  fg: string;
  border: string;
} {
  const raw =
    (item.extracted_highlights?.page_integrity as PageIntegrityStatus | undefined) ??
    item.page_integrity ??
    (isSegmentationHold(item) ? "unverified" : "verified_complete");
  if (raw === "missing_pages_detected") {
    return {
      status: "missing_pages_detected",
      label: "Missing Pages Alert",
      bg: "#fef2f2",
      fg: "#b91c1c",
      border: "#fecaca",
    };
  }
  if (raw === "screenshot_warning") {
    return {
      status: "screenshot_warning",
      label: "Screenshot Warning",
      bg: "#fffbeb",
      fg: "#b45309",
      border: "#fde68a",
    };
  }
  return {
    status: raw,
    label: "All Pages Verified",
    bg: "#ecfdf5",
    fg: "#047857",
    border: "#a7f3d0",
  };
}

function getItemNativeSeal(item: SmartDropItem): string | null {
  const seal =
    item.extracted_highlights?.native_signature_seal ??
    item.extracted_highlights?.ls2_native_signature ??
    item.native_signature_seal ??
    null;
  return typeof seal === "string" && seal.trim() ? seal.trim() : null;
}

export function LoSmartDropButton({
  personId,
  loName,
  companyId,
  companyName,
  dealLinkId,
  compact = false,
}: LoSmartDropButtonProps) {
  const [open, setOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<PortalRole>("broker_lo");
  const [recipientEmail, setRecipientEmail] = useState(
    `${loName.toLowerCase().replace(/[^a-z0-9]+/g, ".")}@partner.example`,
  );
  const [activeLink, setActiveLink] = useState<SmartDropLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [items, setItems] = useState<SmartDropItem[]>(() =>
    getCachedSmartDropItems({ person_id: personId }),
  );

  useEffect(() => {
    setItems(getCachedSmartDropItems({ person_id: personId }));
  }, [personId]);

  async function generateLinkForRole(role: PortalRole) {
    setBusy(true);
    setCopied(false);
    setErrorMsg(null);
    try {
      const link = await createSmartDropLink({
        portal_role: role,
        recipient_email: recipientEmail.trim() || undefined,
        person_id: personId,
        company_id: companyId ?? undefined,
        lo_name: loName,
        company_name: companyName ?? undefined,
        deal_link_id: dealLinkId ?? undefined,
        label: `${loName} · ${PORTAL_ROLE_LABELS[role]} Smart Drop`,
      });
      setActiveLink(link);
      const url = buildSmartDropUrl(link.token);
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
        } catch {
          // Clipboard permission may be restricted in headless environments
        }
      }
      const inbox = await fetchSmartDropInbox({ person_id: personId });
      setItems(inbox.items);
    } catch (err) {
      setActiveLink(null);
      setErrorMsg(err instanceof Error ? err.message : "Unable to generate Smart Drop link");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleOpen(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    await generateLinkForRole(selectedRole);
  }

  async function handleRoleSwitch(e: React.MouseEvent, role: PortalRole) {
    e.stopPropagation();
    setSelectedRole(role);
    await generateLinkForRole(role);
  }

  async function handleCopyUrl(e: React.MouseEvent) {
    e.stopPropagation();
    if (!activeLink) return;
    const url = buildSmartDropUrl(activeLink.token);
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Ignore clipboard errors in non-secure test contexts
      }
    }
    setCopied(true);
  }

  const borrowerGroups = groupItemsByBorrower(items);
  const shareUrl = activeLink ? buildSmartDropUrl(activeLink.token) : "";
  const sharePosUrl = activeLink ? buildSmartPosUrl(activeLink.token) : "";
  const shareSignUrl = activeLink ? buildSmartSignUrl(activeLink.token) : "";
  const relativeDropPath = activeLink
    ? `/drop/${encodeURIComponent(activeLink.token)}`
    : "/drop";
  const relativePosPath = activeLink
    ? `/pos/${encodeURIComponent(activeLink.token)}`
    : "/pos";
  const relativeSignPath = activeLink
    ? `/sign/${encodeURIComponent(activeLink.token)}`
    : "/sign";

  return (
    <div
      className="lo-smart-drop-widget"
      style={{ display: "inline-block", position: "relative" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={handleToggleOpen}
        data-testid={`lo-smart-drop-btn-${personId}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: compact ? "3px 9px" : "6px 11px",
          fontSize: compact ? 11.5 : 12.5,
          fontWeight: 600,
          borderRadius: 6,
          border: "1px solid #c7d2fe",
          background: open ? "#e0e7ff" : "#eef2ff",
          color: "#3730a3",
          cursor: "pointer",
          lineHeight: 1.3,
        }}
        title={`Generate Verified Email Smart Link for ${loName}`}
      >
        <span>Smart Drop Link</span>
        {items.length > 0 && (
          <span
            style={{
              background: "#4338ca",
              color: "#fff",
              borderRadius: 999,
              padding: "1px 6px",
              fontSize: 10.5,
              fontWeight: 700,
            }}
          >
            {items.length}
          </span>
        )}
      </button>

      {open && (
        <div
          role="region"
          aria-label={`Smart Drop Portal Link for ${loName}`}
          data-testid={`lo-smart-drop-popover-${personId}`}
          style={{
            marginTop: 8,
            padding: 12,
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.14)",
            minWidth: 320,
            maxWidth: 420,
            zIndex: 30,
            textAlign: "left",
            color: "#0f172a",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                Email Smart Link · {loName}
              </div>
              <div style={{ fontSize: 11, color: "#64748b" }}>
                {companyName || "External Partner"} · Verified Email Sign-In · Auto-routes to AE / CA / AM / Processor
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close Smart Drop popover"
              style={{
                border: "none",
                background: "transparent",
                color: "#64748b",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 700,
                padding: "2px 6px",
              }}
            >
              ×
            </button>
          </div>

          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#334155", marginBottom: 8 }}>
            Recipient Email (Verified Email Smart Link Sign-In)
            <input
              type="email"
              aria-label="Recipient Email"
              data-testid="lo-smart-link-recipient-email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="broker@company.com"
              style={{
                display: "block",
                width: "100%",
                marginTop: 3,
                padding: "4px 6px",
                fontSize: 11.5,
                borderRadius: 6,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#0f172a",
              }}
            />
          </label>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              marginBottom: 10,
            }}
          >
            {PORTAL_ROLE_OPTIONS.map((opt) => {
              const active = selectedRole === opt.role;
              return (
                <button
                  key={opt.role}
                  type="button"
                  onClick={(e) => handleRoleSwitch(e, opt.role)}
                  style={{
                    fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    padding: "4px 8px",
                    borderRadius: 6,
                    border: active ? "1px solid #4f46e5" : "1px solid #e2e8f0",
                    background: active ? "#eef2ff" : "#f8fafc",
                    color: active ? "#312e81" : "#475569",
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              marginBottom: 10,
            }}
          >
            {errorMsg && (
              <div
                role="alert"
                data-testid="smart-drop-error"
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "#b91c1c",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  padding: "6px 8px",
                  marginBottom: 8,
                }}
              >
                {errorMsg}
              </div>
            )}
            <div
              style={{
                fontSize: 11,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                color: errorMsg ? "#b91c1c" : "#1e293b",
                wordBreak: "break-all",
                marginBottom: 8,
              }}
              data-testid="smart-drop-generated-url"
            >
              {busy
                ? "Generating Email Smart Link..."
                : errorMsg
                  ? `Error: ${errorMsg}`
                  : shareUrl || "Ready to generate"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleCopyUrl}
                disabled={!activeLink || busy}
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: "4px 10px",
                  borderRadius: 6,
                  border: "1px solid #4338ca",
                  background: "#4338ca",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
              >
                {copied ? "Copied Link!" : "Copy Link"}
              </button>
              {activeLink && (
                <a
                  href={relativeDropPath}
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "#2563eb",
                    textDecoration: "none",
                  }}
                >
                  Open Portal Preview ↗
                </a>
              )}
            </div>
            {activeLink && (
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px dashed #cbd5e1",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  fontSize: 10.5,
                }}
              >
                <div
                  data-testid="smart-pos-generated-url"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 6,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    color: "#0369a1",
                  }}
                >
                  <span>Co-Branded POS: {sharePosUrl}</span>
                  <a
                    href={relativePosPath}
                    style={{
                      fontFamily: "sans-serif",
                      fontWeight: 700,
                      color: "#0284c7",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Open POS ↗
                  </a>
                </div>
                <div
                  data-testid="smart-sign-generated-url"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 6,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    color: "#047857",
                  }}
                >
                  <span>Native e-Sign: {shareSignUrl}</span>
                  <a
                    href={relativeSignPath}
                    style={{
                      fontFamily: "sans-serif",
                      fontWeight: 700,
                      color: "#059669",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Open e-Sign ↗
                  </a>
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              borderTop: "1px solid #e2e8f0",
              paddingTop: 8,
              fontSize: 11.5,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span style={{ fontWeight: 600, color: "#334155" }}>
                Incoming Files ({items.length})
              </span>
              <a
                href={`/smart-drops?person_id=${encodeURIComponent(personId)}`}
                style={{ color: "#4338ca", fontWeight: 600, textDecoration: "none" }}
              >
                Open Triage Hub →
              </a>
            </div>
            {borrowerGroups.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 11 }}>
                No files dropped yet. Any PDF, 3.4 XML, or bank statement dropped via this link auto-groups by borrower.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {borrowerGroups.slice(0, 3).map((group) => (
                  <div
                    key={group.borrowerKey}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "4px 8px",
                      borderRadius: 6,
                      background: "#f1f5f9",
                      fontSize: 11,
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#0f172a" }}>
                      {group.borrowerName} ({group.items.length})
                    </span>
                    <span style={{ color: "#475569" }}>
                      {group.routedSeats.join(" · ")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export interface LoanSmartDropPanelProps {
  loanId: string;
  loanNumber?: string | null;
  borrowerName?: string | null;
  loPersonId?: string | null;
  loName?: string | null;
  companyName?: string | null;
  conditionId?: string | null;
  defaultRole?: UploaderRole;
}

export function LoanSmartDropPanel({
  loanId,
  loanNumber,
  borrowerName,
  loPersonId,
  loName,
  companyName,
  conditionId,
  defaultRole,
}: LoanSmartDropPanelProps) {
  const fileInputId = useId();
  const initialRole: UploaderRole = defaultRole ?? (conditionId ? "am" : "ca");
  const [staffRole, setStaffRole] = useState<UploaderRole>(initialRole);
  const [portalRole, setPortalRole] = useState<PortalRole>(
    conditionId ? "borrower" : "broker_lo",
  );
  const [recipientEmail, setRecipientEmail] = useState(
    loName
      ? `${loName.toLowerCase().replace(/[^a-z0-9]+/g, ".")}@partner.example`
      : "partner@company.example",
  );
  const [borrowerInput, setBorrowerInput] = useState(borrowerName ?? "");
  const [items, setItems] = useState<SmartDropItem[]>(() =>
    getCachedSmartDropItems({ loan_id: loanId }),
  );
  const [generatedLink, setGeneratedLink] = useState<SmartDropLink | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewNames, setPreviewNames] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchSmartDropInbox({ loan_id: loanId })
      .then((res) => {
        if (!cancelled) {
          setItems(res.items);
        }
      })
      .catch(() => {
        // Fallback already handled in smartDrop.ts
      });
    return () => {
      cancelled = true;
    };
  }, [loanId]);

  async function handleFilesSelected(fileList: FileList | File[] | null) {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setPreviewNames(files.map((f) => f.name));
    setUploading(true);
    setStatusMessage(null);
    setErrorMessage(null);
    try {
      const created = await uploadStaffSmartDrop({
        files,
        uploader_role: staffRole,
        uploader_name: UPLOADER_ROLE_LABELS[staffRole],
        loan_id: loanId,
        person_id: loPersonId ?? undefined,
        condition_id: conditionId ?? undefined,
        borrower_name: (borrowerInput.trim() || borrowerName) ?? undefined,
      });
      const refreshed = await fetchSmartDropInbox({ loan_id: loanId });
      setItems(refreshed.items.length > 0 ? refreshed.items : created);
      setStatusMessage(
        `Uploaded & classified ${created.length} file(s) with SHA-256 provenance.`,
      );
      setPreviewNames([]);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleGenerateScopedLink() {
    setCopiedLink(false);
    setErrorMessage(null);
    try {
      const link = await createSmartDropLink({
        portal_role: portalRole,
        recipient_email: recipientEmail.trim() || undefined,
        loan_id: loanId,
        person_id: loPersonId ?? undefined,
        condition_id: conditionId ?? undefined,
        lo_name: loName ?? undefined,
        company_name: companyName ?? undefined,
        borrower_name: (borrowerInput.trim() || borrowerName) ?? undefined,
        label: `Loan #${loanNumber || loanId} · ${PORTAL_ROLE_LABELS[portalRole]} Drop`,
      });
      setGeneratedLink(link);
      const url = buildSmartDropUrl(link.token);
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          setCopiedLink(true);
        } catch {
          // Ignore clipboard errors in headless environments
        }
      }
    } catch (err) {
      setGeneratedLink(null);
      setErrorMessage(err instanceof Error ? err.message : "Unable to generate Smart Drop link");
    }
  }

  async function handleBindItem(item: SmartDropItem, attachCondition: boolean) {
    setErrorMessage(null);
    try {
      const updated = await updateSmartDropItem(item.id, {
        loan_id: loanId,
        condition_id: attachCondition
          ? (conditionId ?? item.condition_id ?? "cond-linked")
          : item.condition_id,
        review_status: "bound_to_loan",
      });
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      setStatusMessage(
        attachCondition
          ? `Attached "${updated.file_name}" to condition and bound to Loan #${loanNumber || loanId}.`
          : `Accepted & bound "${updated.file_name}" to Loan #${loanNumber || loanId}.`,
      );
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Unable to update Smart Drop item");
    }
  }

  return (
    <section
      className="loan-smart-drop-panel"
      aria-label="Smart Document Drop & Multi-Role Intake"
      data-testid={`loan-smart-drop-panel-${loanId}`}
      style={{
        marginTop: 14,
        marginBottom: 14,
        padding: 14,
        borderRadius: 10,
        border: "1px solid #cbd5e1",
        background: "#f8fafc",
        color: "#0f172a",
        colorScheme: "light",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
            Smart Document Drop · Loan #{loanNumber || loanId}
            {conditionId ? ` · Condition #${conditionId}` : ""}
          </div>
          <div style={{ fontSize: 11.5, color: "#475569" }}>
            Multi-role intake (AE · CA · AM · Processor) &amp; scoped Verified Email Smart Links
          </div>
        </div>
        <a
          href={`/smart-drops?loan_id=${encodeURIComponent(loanId)}`}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#4338ca",
            textDecoration: "none",
          }}
        >
          Open Triage Hub ({items.length}) →
        </a>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 12,
          marginBottom: 12,
        }}
      >
        {/* Internal Staff Multi-Role Upload */}
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
            Internal Staff Drop (AE / CA / AM / Processor)
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: "#475569", flex: "1 1 140px" }}>
              Uploading Seat
              <select
                aria-label="Uploading Seat"
                value={staffRole}
                onChange={(e) => setStaffRole(e.target.value as UploaderRole)}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 2,
                  padding: "4px 6px",
                  fontSize: 11.5,
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                }}
              >
                {STAFF_ROLE_OPTIONS.map((opt) => (
                  <option key={opt.role} value={opt.role}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 11, color: "#475569", flex: "1 1 140px" }}>
              Borrower Tag
              <input
                type="text"
                aria-label="Borrower Tag"
                placeholder="Auto-detect from file"
                value={borrowerInput}
                onChange={(e) => setBorrowerInput(e.target.value)}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 2,
                  padding: "4px 6px",
                  fontSize: 11.5,
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                }}
              />
            </label>
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void handleFilesSelected(e.dataTransfer.files);
            }}
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px dashed #94a3b8",
              background: "#f8fafc",
              textAlign: "center",
            }}
          >
            <input
              id={fileInputId}
              type="file"
              multiple
              aria-label="Drop or select loan documents"
              onChange={(e) => void handleFilesSelected(e.target.files)}
              style={{ display: "none" }}
            />
            <label
              htmlFor={fileInputId}
              style={{
                cursor: "pointer",
                fontSize: 11.5,
                fontWeight: 600,
                color: "#3730a3",
              }}
            >
              {uploading
                ? "Classifying & hashing files..."
                : "Drop PDF, 3.4 XML, Bank Stmts, Appraisal or Click to Browse"}
            </label>
            {previewNames.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#475569" }}>
                {previewNames.map((name) => {
                  const preview = previewFileClassification(name);
                  return (
                    <div key={name}>
                      {name} → {preview.doc_type_label} ({preview.routed_seat} ·{" "}
                      {LAYA_FAMILY_LABELS[preview.laya_family]})
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Scoped External Email Smart Link */}
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
            Scoped Email Smart Link (Partner / Borrower)
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: "#475569", flex: "1 1 130px" }}>
              External Recipient Role
              <select
                aria-label="External Recipient Role"
                value={portalRole}
                onChange={(e) => setPortalRole(e.target.value as PortalRole)}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 2,
                  padding: "4px 6px",
                  fontSize: 11.5,
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                }}
              >
                {PORTAL_ROLE_OPTIONS.map((opt) => (
                  <option key={opt.role} value={opt.role}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 11, color: "#475569", flex: "1 1 150px" }}>
              Recipient Email
              <input
                type="email"
                aria-label="Recipient Email"
                data-testid="loan-smart-link-recipient-email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="partner@company.com"
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 2,
                  padding: "4px 6px",
                  fontSize: 11.5,
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => void handleGenerateScopedLink()}
              style={{
                padding: "5px 10px",
                fontSize: 11.5,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid #4338ca",
                background: "#4338ca",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              Generate Scoped Email Smart Link
            </button>
          </div>
          {generatedLink ? (
            <div
              style={{
                padding: "6px 8px",
                borderRadius: 6,
                background: "#eef2ff",
                border: "1px solid #c7d2fe",
                fontSize: 11,
              }}
            >
              <div
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  color: "#1e1b4b",
                  wordBreak: "break-all",
                  marginBottom: 4,
                }}
              >
                {buildSmartDropUrl(generatedLink.token)}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ color: "#4338ca", fontWeight: 600 }}>
                  {copiedLink ? "Copied to clipboard" : "Link ready"}
                </span>
                <a
                  href={`/drop/${encodeURIComponent(generatedLink.token)}`}
                  style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}
                >
                  Open Portal Preview ↗
                </a>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "#64748b" }}>
              Generates a Verified Email Smart Link pre-bound to Loan #{loanNumber || loanId}
              {conditionId ? ` and Condition #${conditionId}` : ""}.
            </div>
          )}
        </div>
      </div>

      {errorMessage && (
        <div
          role="alert"
          data-testid="loan-smart-drop-error"
          style={{
            marginBottom: 10,
            padding: "6px 10px",
            borderRadius: 6,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          {errorMessage}
        </div>
      )}

      {statusMessage && (
        <div
          style={{
            marginBottom: 10,
            padding: "6px 10px",
            borderRadius: 6,
            background: "#ecfdf5",
            border: "1px solid #a7f3d0",
            color: "#065f46",
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          {statusMessage}
        </div>
      )}

      {/* Incoming Dropped Files for this Loan */}
      {items.length > 0 && (
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#334155", marginBottom: 6 }}>
            Dropped Documents & Triage Routing ({items.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map((item) => {
              const tone = seatBadgeTone(item.routed_seat);
              const layaFamilyLabel = getItemLayaFamily(item);
              const statementPeriod = getItemStatementPeriod(item);
              const segHold = isSegmentationHold(item);
              const pageIntegrity = getItemPageIntegrity(item);
              const nativeSeal = getItemNativeSeal(item);
              return (
                <div
                  key={item.id}
                  data-testid={`loan-smart-drop-item-${item.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 8,
                    padding: "8px 10px",
                    borderRadius: 8,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    fontSize: 11.5,
                  }}
                >
                  <div style={{ minWidth: 200, flex: 1 }}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                      {item.file_name}{" "}
                      <span style={{ fontWeight: 500, color: "#475569" }}>
                        · {item.doc_type_label}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        alignItems: "center",
                        fontSize: 11,
                        color: "#64748b",
                        marginTop: 4,
                      }}
                    >
                      <span>
                        Borrower: <strong>{item.borrower_name}</strong>
                      </span>
                      <span>· {FOUR_LEGS_LABELS[item.four_legs_pillar]}</span>
                      <span
                        data-testid={`laya-family-badge-${item.id}`}
                        style={{
                          padding: "1px 6px",
                          borderRadius: 4,
                          background: "#f1f5f9",
                          color: "#334155",
                          fontWeight: 600,
                        }}
                      >
                        {layaFamilyLabel}
                      </span>
                      <span
                        data-testid={`page-integrity-badge-${item.id}`}
                        style={{
                          padding: "1px 6px",
                          borderRadius: 4,
                          background: pageIntegrity.bg,
                          color: pageIntegrity.fg,
                          border: `1px solid ${pageIntegrity.border}`,
                          fontWeight: 700,
                        }}
                      >
                        {pageIntegrity.label}
                      </span>
                      {nativeSeal && (
                        <span
                          data-testid={`native-seal-badge-${item.id}`}
                          style={{
                            padding: "1px 6px",
                            borderRadius: 4,
                            background: "#ecfdf5",
                            color: "#065f46",
                            border: "1px solid #6ee7b7",
                            fontWeight: 700,
                          }}
                        >
                          LS2 Native e-Sign Seal · {nativeSeal}
                        </span>
                      )}
                      {statementPeriod && (
                        <span
                          data-testid={`statement-period-badge-${item.id}`}
                          style={{
                            padding: "1px 6px",
                            borderRadius: 4,
                            background: "#e0f2fe",
                            color: "#0369a1",
                            fontWeight: 600,
                          }}
                        >
                          Period: {statementPeriod}
                        </span>
                      )}
                      {segHold && (
                        <span
                          data-testid={`segmentation-hold-badge-${item.id}`}
                          style={{
                            padding: "1px 6px",
                            borderRadius: 4,
                            background: "#fef2f2",
                            color: "#b91c1c",
                            border: "1px solid #fecaca",
                            fontWeight: 700,
                          }}
                        >
                          Mixed Page · Segmentation Hold
                        </span>
                      )}
                      <span>
                        · {DOC_CATEGORY_LABELS[item.doc_category]} ·{" "}
                        {REVIEW_STATUS_LABELS[item.review_status]} · SHA-256{" "}
                        <code>{item.sha256.slice(0, 10)}</code>
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 999,
                        fontSize: 10.5,
                        fontWeight: 700,
                        background: tone.bg,
                        color: tone.fg,
                        border: `1px solid ${tone.border}`,
                      }}
                      title={item.routing_reason}
                    >
                      {ROUTED_SEAT_LABELS[item.routed_seat]}
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleBindItem(item, false)}
                      style={{
                        padding: "4px 8px",
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        background: "#f8fafc",
                        color: "#0f172a",
                        cursor: "pointer",
                      }}
                    >
                      Accept & Bind to Loan
                    </button>
                    {conditionId && (
                      <button
                        type="button"
                        onClick={() => void handleBindItem(item, true)}
                        style={{
                          padding: "4px 8px",
                          fontSize: 11,
                          fontWeight: 600,
                          borderRadius: 6,
                          border: "1px solid #059669",
                          background: "#ecfdf5",
                          color: "#065f46",
                          cursor: "pointer",
                        }}
                      >
                        Attach to Condition
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
